import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { Applicant, ProfileCompleteness, UpdateApplicantProfileRequest } from '../types';
import ProfileDocuments from '../components/ProfileDocuments';
import ValidIdUpload from '../components/ValidIdUpload';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import Modal from '../components/Modal';
import ChangePasswordForm from '../components/ChangePasswordForm';
import {
  SEX_OPTIONS,
  CIVIL_STATUS_OPTIONS,
  SUFFIX_OPTIONS,
  ID_TYPE_OPTIONS,
  SECTORAL_CLASSIFICATIONS,
  PARENTAL_STATUS_OPTIONS,
  SENIOR_HIGH_ALS_YEAR_LEVELS,
  COLLEGE_YEAR_LEVELS,
  PROFESSIONAL_YEAR_LEVELS,
  SPECIAL_COURSE_OPTIONS,
  SCHOOL_TYPE_OPTIONS,
  ACADEMIC_STATUS_OPTIONS
} from '../constants/profileOptions';

const CERTIFICATION_TEXT =
  'I hereby Certify that all the information I have provided is true and correct to the best of my knowledge. I further certify that all Documents attached are authentic, complete, and have not been altered or falsified.';

// Free-text fields uppercased as the applicant types, so what's stored —
// and therefore what prints on the generated application form — matches
// the all-caps convention the office expects.
//
// Deliberately an allow-list rather than a deny-list: a field left out of
// this set simply isn't uppercased (cosmetic), whereas wrongly including
// one would be a real bug — a <select>'s value would stop matching its
// <option value="...">, silently blanking the dropdown. So every
// dropdown-backed field (sex, suffix, civilStatus, academicStatus,
// schoolType, parentalStatus, yearLevel, the yes/no pairs) is omitted, as
// are contactEmail and the numeric amounts/counts.
const UPPERCASE_FIELDS = new Set<string>([
  'firstName',
  'lastName',
  'middleName',
  'placeOfBirth',
  'nationality',
  'ipGroupTribe',
  'idNumber',
  'address',
  'barangay',
  'municipality',
  'province',
  'contactNumber',
  'messengerAccount',
  'courseName',
  'schoolName',
  'schoolAddress',
  'honorsAwards',
  'academicDistinctionExtracurricular',
  'currentAssistanceProgram',
  'otherScholarshipProgram',
  'lbpAtmAccountNumber',
  'sectoralClassificationOther'
]);

// Same idea for the parent/guardian sub-forms — all plain text inputs
// except monthlyIncome, which is numeric.
const UPPERCASE_FAMILY_FIELDS = new Set<string>(['name', 'occupation', 'educationalAttainment', 'relationship']);

// Local, string-only mirror of UpdateApplicantProfileRequest so every
// input can stay a simple controlled <input>/<select> — converted to the
// real request shape only at submit time.
interface FamilyMemberForm {
  name: string;
  occupation: string;
  monthlyIncome: string;
  educationalAttainment: string;
  // Only shown/edited for guardian — father/mother's relationship is fixed.
  relationship: string;
}

interface FormState {
  firstName: string;
  lastName: string;
  middleName: string;
  suffix: string;
  dateOfBirth: string;
  age: string;
  sex: string;
  civilStatus: string;
  contactNumber: string;
  contactEmail: string;
  messengerAccount: string;
  address: string;
  schoolName: string;
  courseName: string;
  yearLevel: string;
  municipality: string;
  barangay: string;
  householdMonthlyIncome: string;
  placeOfBirth: string;
  nationality: string;
  idType: string;
  idNumber: string;
  isIndigenousPeople: string; // '', 'yes', 'no'
  ipGroupTribe: string;
  province: string;
  sectoralClassifications: string[];
  sectoralClassificationOther: string;
  numberOfHouseholdMembers: string;
  numberOfDependentsStudying: string;
  parentalStatus: string;
  schoolAddress: string;
  schoolType: string;
  gwa: string;
  honorsAwards: string;
  academicStatus: string;
  currentlyReceivingAssistance: string;
  currentAssistanceProgram: string;
  currentAssistanceAmount: string;
  appliedOtherScholarship: string;
  otherScholarshipProgram: string;
  receivedCeapAssistance: string;
  ceapAssistanceAmount: string;
  academicDistinctionExtracurricular: string;
  lbpAtmAccountNumber: string;
  father: FamilyMemberForm;
  mother: FamilyMemberForm;
  guardian: FamilyMemberForm;
}

const emptyFamilyMember: FamilyMemberForm = { name: '', occupation: '', monthlyIncome: '', educationalAttainment: '', relationship: '' };

const emptyForm: FormState = {
  firstName: '',
  lastName: '',
  middleName: '',
  suffix: '',
  dateOfBirth: '',
  age: '',
  sex: '',
  civilStatus: '',
  contactNumber: '',
  contactEmail: '',
  messengerAccount: '',
  address: '',
  schoolName: '',
  courseName: '',
  yearLevel: '',
  municipality: 'Conner',
  barangay: '',
  householdMonthlyIncome: '',
  placeOfBirth: '',
  nationality: '',
  idType: '',
  idNumber: '',
  isIndigenousPeople: '',
  ipGroupTribe: '',
  province: 'Apayao',
  sectoralClassifications: [],
  sectoralClassificationOther: '',
  numberOfHouseholdMembers: '',
  numberOfDependentsStudying: '',
  parentalStatus: '',
  schoolAddress: '',
  schoolType: '',
  gwa: '',
  honorsAwards: '',
  academicStatus: '',
  currentlyReceivingAssistance: '',
  currentAssistanceProgram: '',
  currentAssistanceAmount: '',
  appliedOtherScholarship: '',
  otherScholarshipProgram: '',
  receivedCeapAssistance: '',
  ceapAssistanceAmount: '',
  academicDistinctionExtracurricular: '',
  lbpAtmAccountNumber: '',
  father: { ...emptyFamilyMember },
  mother: { ...emptyFamilyMember },
  guardian: { ...emptyFamilyMember }
};

function toDateInputValue(value?: Date | string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function toYesNo(value?: boolean): string {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '';
}

// Age is derived from Date of Birth rather than typed in directly, so the
// two values can never drift apart.
function calculateAge(dateOfBirth: string): string {
  if (!dateOfBirth) return '';
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const hasHadBirthdayThisYear =
    today.getMonth() > dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
  if (!hasHadBirthdayThisYear) age -= 1;
  return age >= 0 ? String(age) : '';
}

function fromYesNo(value: string): boolean | undefined {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
}

function toFamilyMemberForm(detail?: Applicant['father']): FamilyMemberForm {
  return {
    name: detail?.name ?? '',
    occupation: detail?.occupation ?? '',
    monthlyIncome: detail?.monthlyIncome !== undefined ? String(detail.monthlyIncome) : '',
    educationalAttainment: detail?.educationalAttainment ?? '',
    relationship: detail?.relationship ?? ''
  };
}

function applicantToForm(applicant: Applicant): FormState {
  return {
    firstName: applicant.firstName ?? '',
    lastName: applicant.lastName ?? '',
    middleName: applicant.middleName ?? '',
    suffix: applicant.suffix ?? '',
    dateOfBirth: toDateInputValue(applicant.dateOfBirth),
    age: calculateAge(toDateInputValue(applicant.dateOfBirth)) || (applicant.age !== undefined ? String(applicant.age) : ''),
    sex: applicant.sex ?? '',
    civilStatus: applicant.civilStatus ?? '',
    contactNumber: applicant.contactNumber ?? '',
    contactEmail: applicant.contactEmail ?? '',
    messengerAccount: applicant.messengerAccount ?? '',
    address: applicant.address ?? '',
    schoolName: applicant.schoolName ?? '',
    courseName: applicant.courseName ?? '',
    yearLevel: applicant.yearLevel ?? '',
    municipality: applicant.municipality || 'Conner',
    barangay: applicant.barangay ?? '',
    householdMonthlyIncome: applicant.householdMonthlyIncome !== undefined ? String(applicant.householdMonthlyIncome) : '',
    placeOfBirth: applicant.placeOfBirth ?? '',
    nationality: applicant.nationality ?? '',
    idType: applicant.idType ?? '',
    idNumber: applicant.idNumber ?? '',
    isIndigenousPeople: toYesNo(applicant.isIndigenousPeople),
    ipGroupTribe: applicant.ipGroupTribe ?? '',
    province: applicant.province || 'Apayao',
    sectoralClassifications: applicant.sectoralClassifications ?? [],
    sectoralClassificationOther: applicant.sectoralClassificationOther ?? '',
    numberOfHouseholdMembers: applicant.numberOfHouseholdMembers !== undefined ? String(applicant.numberOfHouseholdMembers) : '',
    numberOfDependentsStudying:
      applicant.numberOfDependentsStudying !== undefined ? String(applicant.numberOfDependentsStudying) : '',
    parentalStatus: applicant.parentalStatus ?? '',
    schoolAddress: applicant.schoolAddress ?? '',
    schoolType: applicant.schoolType ?? '',
    gwa: applicant.gwa !== undefined ? String(applicant.gwa) : '',
    honorsAwards: applicant.honorsAwards ?? '',
    academicStatus: applicant.academicStatus ?? '',
    currentlyReceivingAssistance: toYesNo(applicant.currentlyReceivingAssistance),
    currentAssistanceProgram: applicant.currentAssistanceProgram ?? '',
    currentAssistanceAmount: applicant.currentAssistanceAmount !== undefined ? String(applicant.currentAssistanceAmount) : '',
    appliedOtherScholarship: toYesNo(applicant.appliedOtherScholarship),
    otherScholarshipProgram: applicant.otherScholarshipProgram ?? '',
    receivedCeapAssistance: toYesNo(applicant.receivedCeapAssistance),
    ceapAssistanceAmount: applicant.ceapAssistanceAmount !== undefined ? String(applicant.ceapAssistanceAmount) : '',
    academicDistinctionExtracurricular: applicant.academicDistinctionExtracurricular ?? '',
    lbpAtmAccountNumber: applicant.lbpAtmAccountNumber ?? '',
    father: toFamilyMemberForm(applicant.father),
    mother: toFamilyMemberForm(applicant.mother),
    guardian: toFamilyMemberForm(applicant.guardian)
  };
}

function formToRequest(form: FormState): UpdateApplicantProfileRequest {
  const num = (v: string) => (v === '' ? undefined : Number(v));
  const familyMember = (m: FamilyMemberForm) =>
    m.name || m.occupation || m.monthlyIncome || m.educationalAttainment || m.relationship
      ? {
          name: m.name || undefined,
          occupation: m.occupation || undefined,
          monthlyIncome: num(m.monthlyIncome),
          educationalAttainment: m.educationalAttainment || undefined,
          relationship: m.relationship || undefined
        }
      : undefined;

  return {
    firstName: form.firstName || undefined,
    lastName: form.lastName || undefined,
    middleName: form.middleName || undefined,
    suffix: form.suffix || undefined,
    dateOfBirth: form.dateOfBirth || undefined,
    age: num(form.age),
    sex: form.sex || undefined,
    civilStatus: form.civilStatus || undefined,
    contactNumber: form.contactNumber || undefined,
    contactEmail: form.contactEmail || undefined,
    messengerAccount: form.messengerAccount || undefined,
    address: form.address || undefined,
    schoolName: form.schoolName || undefined,
    courseName: form.courseName || undefined,
    yearLevel: form.yearLevel || undefined,
    municipality: form.municipality || undefined,
    barangay: form.barangay || undefined,
    householdMonthlyIncome: num(form.householdMonthlyIncome),
    placeOfBirth: form.placeOfBirth || undefined,
    nationality: form.nationality || undefined,
    idType: form.idType || undefined,
    idNumber: form.idNumber || undefined,
    isIndigenousPeople: fromYesNo(form.isIndigenousPeople),
    ipGroupTribe: form.ipGroupTribe || undefined,
    province: form.province || undefined,
    sectoralClassifications: form.sectoralClassifications,
    sectoralClassificationOther: form.sectoralClassificationOther || undefined,
    numberOfHouseholdMembers: num(form.numberOfHouseholdMembers),
    numberOfDependentsStudying: num(form.numberOfDependentsStudying),
    parentalStatus: form.parentalStatus || undefined,
    schoolAddress: form.schoolAddress || undefined,
    schoolType: form.schoolType || undefined,
    gwa: num(form.gwa),
    honorsAwards: form.honorsAwards || undefined,
    academicStatus: form.academicStatus || undefined,
    currentlyReceivingAssistance: fromYesNo(form.currentlyReceivingAssistance),
    currentAssistanceProgram: form.currentAssistanceProgram || undefined,
    currentAssistanceAmount: num(form.currentAssistanceAmount),
    appliedOtherScholarship: fromYesNo(form.appliedOtherScholarship),
    otherScholarshipProgram: form.otherScholarshipProgram || undefined,
    receivedCeapAssistance: fromYesNo(form.receivedCeapAssistance),
    ceapAssistanceAmount: num(form.ceapAssistanceAmount),
    academicDistinctionExtracurricular: form.academicDistinctionExtracurricular || undefined,
    lbpAtmAccountNumber: form.lbpAtmAccountNumber || undefined,
    father: familyMember(form.father),
    mother: familyMember(form.mother),
    guardian: familyMember(form.guardian)
  };
}

const ProfilePage = () => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [completeness, setCompleteness] = useState<ProfileCompleteness | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [downloadingForm, setDownloadingForm] = useState(false);
  // Must be re-checked before every save (not persisted) — an affirmative,
  // in-the-moment attestation that what's about to be saved is accurate.
  const [certified, setCertified] = useState(false);
  // Gates the Download Application Form button. The PDF is rendered from
  // the *stored* profile, so ticking the certification alone isn't enough
  // — it would hand back a form that doesn't match what's on screen.
  // Set only by a successful save, and cleared again by the next edit, so
  // a downloaded form is always a certified, saved profile.
  const [certifiedAndSaved, setCertifiedAndSaved] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);
  // Not part of FormState (not user-editable) — set once from the loaded
  // profile and used purely to disable the form/Save button.
  const [profileLocked, setProfileLocked] = useState(false);
  // Tracks an explicit "Other" click on the Special Course sub-dropdown,
  // independent of courseName — courseName is cleared to '' right after
  // choosing "Other" (so the text field starts blank), and deriving the
  // dropdown's displayed value purely from courseName would snap it back
  // to "Select..." the instant it's cleared, making "Other" look
  // unclickable.
  const [specialCourseOtherSelected, setSpecialCourseOtherSelected] = useState(false);
  // Same "Others" -> text field swap as specialCourseOtherSelected above,
  // for the Type of ID dropdown.
  const [idTypeOtherSelected, setIdTypeOtherSelected] = useState(false);


  const loadCompleteness = async () => {
    try {
      setCompleteness(await apiService.getProfileCompleteness());
    } catch {
      // Non-fatal — the banner just won't show if this fails.
    }
  };

  const loadAll = async () => {
    setError('');
    try {
      const [profile] = await Promise.all([apiService.getMyProfile(), loadCompleteness()]);
      setForm(applicantToForm(profile));
      setProfileLocked(profile.profileLocked);
      setSpecialCourseOtherSelected(false);
      setIdTypeOtherSelected(false);
      setCertifiedAndSaved(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every edit path re-locks the download — what's on screen no longer
  // matches the saved profile the PDF would be rendered from.
  const markDirty = () => setCertifiedAndSaved(false);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      [key]: UPPERCASE_FIELDS.has(key as string) && typeof value === 'string' ? value.toUpperCase() : value
    }));
  };

  const setFamilyField = (who: 'father' | 'mother' | 'guardian', field: keyof FamilyMemberForm, value: string) => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      [who]: { ...prev[who], [field]: UPPERCASE_FAMILY_FIELDS.has(field) ? value.toUpperCase() : value }
    }));
  };

  const toggleSectoral = (option: string) => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      sectoralClassifications: prev.sectoralClassifications.includes(option)
        ? prev.sectoralClassifications.filter((o) => o !== option)
        : [...prev.sectoralClassifications, option]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!certified) {
      setError('Please check the certification box before saving your profile.');
      return;
    }
    setSaving(true);
    try {
      const updated = await apiService.updateMyProfile(formToRequest(form));
      setForm(applicantToForm(updated));
      await loadCompleteness();
      setCertified(false);
      // Saved *with* the certification ticked — the download is now
      // backed by a stored, certified profile.
      setCertifiedAndSaved(true);
      setShowSavedModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadApplicationForm = async () => {
    setDownloadingForm(true);
    setError('');
    try {
      const blob = await apiService.downloadApplicationFormPdf();
      const objectUrl = URL.createObjectURL(blob);
      // A direct <a download> click triggers the browser's native download,
      // unlike window.open(blobUrl, '_blank') — mobile browsers (Android
      // Chrome in particular) treat that as a popup once the surrounding
      // await has broken the "same tick as the click" user-activation
      // window, and silently block it or open a blank tab instead.
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${form.firstName || 'Application'}-${form.lastName || 'Form'}-Application-Form.pdf`.replace(/\s+/g, '-');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err: any) {
      setError(err.message || 'Failed to generate application form');
    } finally {
      setDownloadingForm(false);
    }
  };

  const isCourseLevel = COLLEGE_YEAR_LEVELS.includes(form.yearLevel);
  // "Special Course" is now the group heading for the Professional/Post
  // Graduate year levels — selecting any of them shows the
  // Juris Doctor/Vet Med/Medicine dropdown instead of a plain Course input.
  const isSpecialCourse = PROFESSIONAL_YEAR_LEVELS.includes(form.yearLevel);
  const specialCourseSelectValue = specialCourseOtherSelected
    ? 'Other'
    : SPECIAL_COURSE_OPTIONS.includes(form.courseName)
      ? form.courseName
      : form.courseName
        ? 'Other'
        : '';
  const hasBothParents = form.father.name.trim() !== '' && form.mother.name.trim() !== '';
  const idTypeSelectValue = idTypeOtherSelected
    ? 'Others'
    : ID_TYPE_OPTIONS.includes(form.idType)
      ? form.idType
      : form.idType
        ? 'Others'
        : '';

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">My Profile</div>
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>My Profile</h1>
          <p>Complete every section below — this is required before you can submit a scholarship application.</p>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <ProfilePictureUpload />

            {completeness && !completeness.complete && (
              <div
                style={{
                  padding: '1rem',
                  background: 'var(--warning-bg)',
                  border: '1px solid var(--warning-border)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                  color: 'var(--warning-text)'
                }}
              >
                <strong>Your profile is incomplete.</strong> The following are still missing:
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                  {[...completeness.missingFields, ...completeness.missingDocuments].map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {completeness && completeness.complete && (
              <div
                style={{
                  padding: '1rem',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                  color: '#34D399'
                }}
              >
                Your profile is complete. You're ready to submit applications.
              </div>
            )}

            {error && (
              <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
                {error}
              </div>
            )}

            {profileLocked && (
              <div
                style={{
                  padding: '1rem',
                  background: 'var(--warning-bg)',
                  border: '1px solid var(--warning-border)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                  color: 'var(--warning-text)'
                }}
              >
                Your profile is locked because you've submitted an application. Ask an administrator to unlock it
                before making changes.
              </div>
            )}

            {/* Sits above every section rather than beside individual
                fields: a blank field is indistinguishable from an
                unanswered one to the reviewer, and the office wants an
                explicit N/A on the printed form either way. */}
            <div
              style={{
                padding: '0.85rem 1rem',
                background: 'rgba(139, 92, 246, 0.08)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: 'var(--radius-md)',
                marginBottom: '1.5rem',
                fontWeight: 600
              }}
            >
              Type "N/A" if it is Not Applicable
            </div>

            <form onSubmit={handleSubmit}>
              <fieldset disabled={profileLocked} style={{ border: 'none', padding: 0, margin: 0 }}>
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>I. Personal Information</h3>
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input id="lastName" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} required autoFocus />
                </div>
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input id="firstName" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label htmlFor="middleName">Middle Name</label>
                  <input id="middleName" value={form.middleName} onChange={(e) => set('middleName', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="suffix">Suffix</label>
                  <select id="suffix" value={form.suffix} onChange={(e) => set('suffix', e.target.value)}>
                    <option value="" disabled hidden>Select...</option>
                    {SUFFIX_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="sex">Sex</label>
                  <select id="sex" value={form.sex} onChange={(e) => set('sex', e.target.value)}>
                    <option value="" disabled hidden>Select...</option>
                    {SEX_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="civilStatus">Civil Status</label>
                  <select id="civilStatus" value={form.civilStatus} onChange={(e) => set('civilStatus', e.target.value)}>
                    <option value="" disabled hidden>Select...</option>
                    {CIVIL_STATUS_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    id="dateOfBirth"
                    type="date"
                    value={form.dateOfBirth}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, dateOfBirth: e.target.value, age: calculateAge(e.target.value) }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="age">Age</label>
                  <input id="age" type="number" value={form.age} disabled />
                  <small style={{ color: 'var(--text-secondary)' }}>Computed automatically from Date of Birth</small>
                </div>
                <div className="form-group">
                  <label htmlFor="placeOfBirth">Place of Birth</label>
                  <input id="placeOfBirth" value={form.placeOfBirth} onChange={(e) => set('placeOfBirth', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="nationality">Nationality</label>
                  <input id="nationality" value={form.nationality} onChange={(e) => set('nationality', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="idType">Type of ID</label>
                  {idTypeOtherSelected ? (
                    <>
                      <input
                        id="idType"
                        value={form.idType}
                        // Uppercased here rather than via UPPERCASE_FIELDS:
                        // idType is a <select> in the other branch, where
                        // uppercasing would break option matching.
                        onChange={(e) => set('idType', e.target.value.toUpperCase())}
                        placeholder="Type your ID type"
                        autoFocus
                      />
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        style={{ marginTop: '0.5rem' }}
                        onClick={() => {
                          setIdTypeOtherSelected(false);
                          set('idType', '');
                        }}
                      >
                        Choose from list instead
                      </button>
                    </>
                  ) : (
                    <select
                      id="idType"
                      value={idTypeSelectValue}
                      onChange={(e) => {
                        const isOther = e.target.value === 'Others';
                        setIdTypeOtherSelected(isOther);
                        set('idType', isOther ? '' : e.target.value);
                      }}
                    >
                      <option value="" disabled hidden>Select...</option>
                      {ID_TYPE_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <ValidIdUpload onChange={loadCompleteness} />
                <div className="form-group">
                  <label htmlFor="idNumber">Valid ID Number</label>
                  <input id="idNumber" value={form.idNumber} onChange={(e) => set('idNumber', e.target.value)} />
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>II. Indigenous Peoples (IP) Affiliation</h3>
                </div>
                <div className="form-group">
                  <label htmlFor="isIndigenousPeople">Are you a member of an Indigenous Peoples (IP) group?</label>
                  <select
                    id="isIndigenousPeople"
                    value={form.isIndigenousPeople}
                    onChange={(e) => set('isIndigenousPeople', e.target.value)}
                  >
                    <option value="" disabled hidden>Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                {form.isIndigenousPeople === 'yes' && (
                  <div className="form-group">
                    <label htmlFor="ipGroupTribe">IP Group/Tribe</label>
                    <input id="ipGroupTribe" value={form.ipGroupTribe} onChange={(e) => set('ipGroupTribe', e.target.value)} />
                  </div>
                )}
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>III. Contact Details</h3>
                </div>
                <div className="form-group">
                  <label htmlFor="address">Home Address (Purok/House No.)</label>
                  <input id="address" value={form.address} onChange={(e) => set('address', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="barangay">Barangay</label>
                  <input id="barangay" value={form.barangay} onChange={(e) => set('barangay', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="municipality">Municipality/City</label>
                  <input id="municipality" value={form.municipality} onChange={(e) => set('municipality', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="province">Province</label>
                  <input id="province" value={form.province} onChange={(e) => set('province', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="contactNumber">Phone No.</label>
                  <input id="contactNumber" value={form.contactNumber} onChange={(e) => set('contactNumber', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="contactEmail">Email Address</label>
                  <input
                    id="contactEmail"
                    type="email"
                    value={form.contactEmail}
                    onChange={(e) => set('contactEmail', e.target.value)}
                  />
                  <small style={{ color: 'var(--text-secondary)' }}>
                    Where we'll send application status updates and new program announcements. Leave blank to use
                    your account email instead.
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="messengerAccount">
                    Messenger Account <span style={{ color: 'var(--error-text)' }}>(Required)</span>
                  </label>
                  <input
                    id="messengerAccount"
                    value={form.messengerAccount}
                    onChange={(e) => set('messengerAccount', e.target.value)}
                  />
                  <small style={{ color: 'var(--text-secondary)' }}>
                    The name on your Facebook Messenger account, or a link to it. This is how the focal persons will
                    usually reach you, so it's required.
                  </small>
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>IV. Socio-economic / Sectoral Classification</h3>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>Check all applicable.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {SECTORAL_CLASSIFICATIONS.map((option) => (
                    <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                      <input
                        type="checkbox"
                        checked={form.sectoralClassifications.includes(option)}
                        onChange={() => toggleSectoral(option)}
                      />
                      {option}
                    </label>
                  ))}
                </div>
                {form.sectoralClassifications.includes('Others') && (
                  <div className="form-group" style={{ marginTop: '0.75rem' }}>
                    <label htmlFor="sectoralOther">Others (please specify)</label>
                    <input
                      id="sectoralOther"
                      placeholder="Type input"
                      value={form.sectoralClassificationOther}
                      onChange={(e) => set('sectoralClassificationOther', e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>V. Family Background</h3>
                </div>

                <h4 style={{ marginTop: 0 }}>Father</h4>
                <div className="form-group">
                  <label htmlFor="fatherName">Father's Name</label>
                  <input id="fatherName" value={form.father.name} onChange={(e) => setFamilyField('father', 'name', e.target.value)} />
                  <small style={{ color: 'var(--text-secondary)' }}>Format: First Name, Middle Initial, Last Name</small>
                </div>
                <div className="form-group">
                  <label htmlFor="fatherOccupation">Occupation</label>
                  <input
                    id="fatherOccupation"
                    value={form.father.occupation}
                    onChange={(e) => setFamilyField('father', 'occupation', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="fatherIncome">Monthly Income</label>
                  <input
                    id="fatherIncome"
                    type="number"
                    value={form.father.monthlyIncome}
                    onChange={(e) => setFamilyField('father', 'monthlyIncome', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="fatherEducation">Educational Attainment</label>
                  <input
                    id="fatherEducation"
                    value={form.father.educationalAttainment}
                    onChange={(e) => setFamilyField('father', 'educationalAttainment', e.target.value)}
                  />
                </div>

                <h4>Mother</h4>
                <div className="form-group">
                  <label htmlFor="motherName">Mother's Maiden Name</label>
                  <input id="motherName" value={form.mother.name} onChange={(e) => setFamilyField('mother', 'name', e.target.value)} />
                  <small style={{ color: 'var(--text-secondary)' }}>Format: First Name, Middle Initial, Last Name</small>
                </div>
                <div className="form-group">
                  <label htmlFor="motherOccupation">Occupation</label>
                  <input
                    id="motherOccupation"
                    value={form.mother.occupation}
                    onChange={(e) => setFamilyField('mother', 'occupation', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="motherIncome">Monthly Income</label>
                  <input
                    id="motherIncome"
                    type="number"
                    value={form.mother.monthlyIncome}
                    onChange={(e) => setFamilyField('mother', 'monthlyIncome', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="motherEducation">Educational Attainment</label>
                  <input
                    id="motherEducation"
                    value={form.mother.educationalAttainment}
                    onChange={(e) => setFamilyField('mother', 'educationalAttainment', e.target.value)}
                  />
                </div>

                <h4>Guardian (if applicable)</h4>
                {hasBothParents && (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
                    Not needed — Father and Mother information is already provided above.
                  </p>
                )}
                <div className="form-group">
                  <label htmlFor="guardianName">Name</label>
                  <input
                    id="guardianName"
                    value={form.guardian.name}
                    onChange={(e) => setFamilyField('guardian', 'name', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="guardianRelationship">Relationship to Applicant</label>
                  <input
                    id="guardianRelationship"
                    value={form.guardian.relationship}
                    onChange={(e) => setFamilyField('guardian', 'relationship', e.target.value)}
                    placeholder="e.g. Grandmother, Aunt, Sibling"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="guardianOccupation">Occupation</label>
                  <input
                    id="guardianOccupation"
                    value={form.guardian.occupation}
                    onChange={(e) => setFamilyField('guardian', 'occupation', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="guardianIncome">Monthly Income (optional)</label>
                  <input
                    id="guardianIncome"
                    type="number"
                    value={form.guardian.monthlyIncome}
                    onChange={(e) => setFamilyField('guardian', 'monthlyIncome', e.target.value)}
                  />
                </div>
                <h4>Household</h4>
                <div className="form-group">
                  <label htmlFor="householdMonthlyIncome">Total Household Monthly Income</label>
                  <input
                    id="householdMonthlyIncome"
                    type="number"
                    value={form.householdMonthlyIncome}
                    onChange={(e) => set('householdMonthlyIncome', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="numberOfHouseholdMembers">Number of Household Members</label>
                  <input
                    id="numberOfHouseholdMembers"
                    type="number"
                    value={form.numberOfHouseholdMembers}
                    onChange={(e) => set('numberOfHouseholdMembers', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="numberOfDependentsStudying">Number of Dependents Studying</label>
                  <input
                    id="numberOfDependentsStudying"
                    type="number"
                    value={form.numberOfDependentsStudying}
                    onChange={(e) => set('numberOfDependentsStudying', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="parentalStatus">Parental Status</label>
                  <select id="parentalStatus" value={form.parentalStatus} onChange={(e) => set('parentalStatus', e.target.value)}>
                    <option value="" disabled hidden>Select...</option>
                    {PARENTAL_STATUS_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>VI. Educational Background</h3>
                </div>
                <div className="form-group">
                  <label htmlFor="schoolName">Current School</label>
                  <input id="schoolName" value={form.schoolName} onChange={(e) => set('schoolName', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="schoolAddress">School Address</label>
                  <input id="schoolAddress" value={form.schoolAddress} onChange={(e) => set('schoolAddress', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="schoolType">Type</label>
                  <select id="schoolType" value={form.schoolType} onChange={(e) => set('schoolType', e.target.value)}>
                    <option value="" disabled hidden>Select...</option>
                    {SCHOOL_TYPE_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="yearLevel">Year Level</label>
                  <select
                    id="yearLevel"
                    value={form.yearLevel}
                    onChange={(e) => {
                      // Clears any leftover Course value from a previous
                      // Year Level — otherwise a stale courseName that
                      // doesn't match Juris Doctor/Vet Med/Medicine makes
                      // the Special Course dropdown falsely default to
                      // "Other" instead of the blank placeholder.
                      set('yearLevel', e.target.value);
                      set('courseName', '');
                      setSpecialCourseOtherSelected(false);
                    }}
                  >
                    <option value="" disabled hidden>Select...</option>
                    <optgroup label="Senior High School / ALS">
                      {SENIOR_HIGH_ALS_YEAR_LEVELS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="College">
                      {COLLEGE_YEAR_LEVELS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Special Course">
                      {PROFESSIONAL_YEAR_LEVELS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                {isSpecialCourse ? (
                  <div className="form-group">
                    <label htmlFor="specialCourseSelect">Special Course</label>
                    {specialCourseOtherSelected ? (
                      <>
                        <input
                          id="specialCourseSelect"
                          value={form.courseName}
                          onChange={(e) => set('courseName', e.target.value)}
                          placeholder="Type your special course"
                          autoFocus
                        />
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          style={{ marginTop: '0.5rem' }}
                          onClick={() => {
                            setSpecialCourseOtherSelected(false);
                            set('courseName', '');
                          }}
                        >
                          Choose from list instead
                        </button>
                      </>
                    ) : (
                      <select
                        id="specialCourseSelect"
                        value={specialCourseSelectValue}
                        onChange={(e) => {
                          const isOther = e.target.value === 'Other';
                          setSpecialCourseOtherSelected(isOther);
                          set('courseName', isOther ? '' : e.target.value);
                        }}
                      >
                        <option value="" disabled hidden>Please Select</option>
                        {SPECIAL_COURSE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                        <option value="Other">Other</option>
                      </select>
                    )}
                  </div>
                ) : (
                  isCourseLevel && (
                    <div className="form-group">
                      <label htmlFor="courseName">Course</label>
                      <input id="courseName" value={form.courseName} onChange={(e) => set('courseName', e.target.value)} />
                    </div>
                  )
                )}
                <div className="form-group">
                  <label htmlFor="gwa">General Weighted Average (GWA)</label>
                  <input id="gwa" type="number" step="0.01" value={form.gwa} onChange={(e) => set('gwa', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="honorsAwards">Honors/Awards Received (optional)</label>
                  <input id="honorsAwards" value={form.honorsAwards} onChange={(e) => set('honorsAwards', e.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor="academicStatus">Academic Status</label>
                  <select id="academicStatus" value={form.academicStatus} onChange={(e) => set('academicStatus', e.target.value)}>
                    <option value="" disabled hidden>Select...</option>
                    {ACADEMIC_STATUS_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>VII. Other Educational Assistance / Scholarships</h3>
                </div>
                <div className="form-group">
                  <label htmlFor="currentlyReceivingAssistance">
                    Are you currently or have you previously received any scholarship or financial assistance?
                  </label>
                  <select
                    id="currentlyReceivingAssistance"
                    value={form.currentlyReceivingAssistance}
                    onChange={(e) => set('currentlyReceivingAssistance', e.target.value)}
                  >
                    <option value="" disabled hidden>Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                {form.currentlyReceivingAssistance === 'yes' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="currentAssistanceProgram">Program/Scholarship</label>
                      <input
                        id="currentAssistanceProgram"
                        value={form.currentAssistanceProgram}
                        onChange={(e) => set('currentAssistanceProgram', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="currentAssistanceAmount">Amount</label>
                      <input
                        id="currentAssistanceAmount"
                        type="number"
                        value={form.currentAssistanceAmount}
                        onChange={(e) => set('currentAssistanceAmount', e.target.value)}
                      />
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label htmlFor="receivedCeapAssistance">
                    Have you received assistance under the Enhanced Conner Educational Assistance Program (ECEAP)?
                  </label>
                  <select
                    id="receivedCeapAssistance"
                    value={form.receivedCeapAssistance}
                    onChange={(e) => set('receivedCeapAssistance', e.target.value)}
                  >
                    <option value="" disabled hidden>Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                {form.receivedCeapAssistance === 'yes' && (
                  <div className="form-group">
                    <label htmlFor="ceapAssistanceAmount">Amount</label>
                    <input
                      id="ceapAssistanceAmount"
                      type="number"
                      value={form.ceapAssistanceAmount}
                      onChange={(e) => set('ceapAssistanceAmount', e.target.value)}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="appliedOtherScholarship">Have you applied for other scholarships/financial assistance?</label>
                  <select
                    id="appliedOtherScholarship"
                    value={form.appliedOtherScholarship}
                    onChange={(e) => set('appliedOtherScholarship', e.target.value)}
                  >
                    <option value="" disabled hidden>Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                {form.appliedOtherScholarship === 'yes' && (
                  <div className="form-group">
                    <label htmlFor="otherScholarshipProgram">Program</label>
                    <input
                      id="otherScholarshipProgram"
                      value={form.otherScholarshipProgram}
                      onChange={(e) => set('otherScholarshipProgram', e.target.value)}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="academicDistinctionExtracurricular">
                    Academic distinction &amp; extracurricular involvement (Sports, Academic and other co-curricular activities)
                  </label>
                  <textarea
                    id="academicDistinctionExtracurricular"
                    value={form.academicDistinctionExtracurricular}
                    onChange={(e) => set('academicDistinctionExtracurricular', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>VIII. Documentary Requirements</h3>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    background: 'rgba(139, 92, 246, 0.05)',
                    borderRadius: 'var(--radius-md)'
                  }}
                >
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    Download and print your pre-filled application form. Attach a 2" x 2" ID photo to the printed form. Submit the original/authenticated hard copy when required.
                    {!certifiedAndSaved && (
                      <>
                        {' '}
                        <strong>
                          Tick the certification below and click Save Profile to enable the download.
                        </strong>
                      </>
                    )}
                  </p>
                  {/* Needs the certification ticked *and* saved: the PDF is
                      rendered server-side from the stored profile, so
                      enabling it on the tick alone would hand back a form
                      that doesn't match the unsaved edits on screen. */}
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={downloadingForm || !certifiedAndSaved}
                    title={!certifiedAndSaved ? 'Tick the certification below and save your profile first' : undefined}
                    onClick={handleDownloadApplicationForm}
                  >
                    {downloadingForm ? 'Generating...' : 'Download Application Form (PDF)'}
                  </button>
                </div>
                <ProfileDocuments onChange={loadCompleteness} />
              </div>

              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                  <h3>IX. ATM Card</h3>
                </div>
                <div className="form-group">
                  <label htmlFor="lbpAtmAccountNumber">LBP ATM Account Number</label>
                  <input
                    id="lbpAtmAccountNumber"
                    value={form.lbpAtmAccountNumber}
                    onChange={(e) => set('lbpAtmAccountNumber', e.target.value)}
                  />
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Upload the LBP ATM Card photocopy under "LBP ATM Card Photocopy" in section VIII above.
                </p>
              </div>

              <div
                className="card"
                style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}
              >
                <input
                  id="certification"
                  type="checkbox"
                  checked={certified}
                  onChange={(e) => setCertified(e.target.checked)}
                  style={{ marginTop: '0.2rem' }}
                />
                <label htmlFor="certification" style={{ fontSize: '0.85rem', fontWeight: 400 }}>
                  {CERTIFICATION_TEXT}
                </label>
              </div>

              <button className="btn btn-primary" type="submit" disabled={saving || !certified || profileLocked}>
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
              </fieldset>
            </form>

            <div className="card" style={{ marginTop: '1.5rem' }}>
              <div className="card-header">
                <h3>Change Password</h3>
              </div>
              <ChangePasswordForm />
            </div>
          </>
        )}
      </div>

      {showSavedModal && (
        <Modal title="Profile is Saved" onClose={() => setShowSavedModal(false)}>
          <p>Your profile has been saved successfully.</p>
          <button className="btn btn-primary" type="button" onClick={() => setShowSavedModal(false)}>
            OK
          </button>
        </Modal>
      )}
    </div>
  );
};

export default ProfilePage;
